
import React, { useState, useEffect, useMemo } from 'react'; // Added useMemo
import { NavBar } from '@/components/navbar';
import PendingInvitationsAlert from '@/components/dashboard/PendingInvitationsAlert';
// import { usePortfolioState } from '@/hooks/usePortfolioState'; // Removed
// import { usePortfolioDebugLogging } from '@/hooks/usePortfolioDebugLogging'; // Removed
import PortfolioPageLayout from '@/components/portfolio/PortfolioPageLayout';
import PortfolioModals from '@/components/portfolio/PortfolioModals';
import { useNavigate } from 'react-router-dom';
import { PortfolioFormValues } from '@/hooks/useAddPortfolioForm'; // This might be PortfolioCreationInput now
import { usePortfolioCreation } from '@/hooks/portfolio/usePortfolioCreation'; // For creating portfolios
import { LoadingScreen } from '@/components/ui/loading-screen';
import { useFirebase } from '@/context/FirebaseProvider'; // Added
import { LayoutMode } from '@/hooks/usePortfolioState'; // Keep for type
import { FilterOptions } from "@/components/FilterModal"; // For local filter state
import { filterPortfolios } from "@/components/portfolio/utils/portfolioFilterUtils"; // For filtering
import { Portfolio } from '@/schemas/portfolio'; // Added missing import

const defaultPortfolioFilters: FilterOptions = {
  portfolios: [],
  status: [],
  industries: [],
};

const Index = () => {
  const navigate = useNavigate();
  const { firebaseUser, userData, portfolios: allPortfoliosFromFirebase, businesses: allBusinesses, isLoading: isLoadingData, error } = useFirebase(); // Added allBusinesses
  
  // UI States - formerly from usePortfolioState and its sub-hooks
  const [searchTerm, setSearchTerm] = useState('');
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>('grid');
  const [filters, setFilters] = useState<FilterOptions>(defaultPortfolioFilters);
  
  const [showMobileSearch, setShowMobileSearch] = useState(false); // Retained from modalState
  const [showFilterButton, setShowFilterButton] = useState(false); // Retained from filterState
  const [showFilterOptions, setShowFilterOptions] = useState(false); // Retained from filterState

  // Modal States
  const [showAddPortfolioModal, setShowAddPortfolioModal] = useState(false);
  const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
  const [showMobileAddBusinessModal, setShowMobileAddBusinessModal] = useState(false);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string | null>(null);

  const { createPortfolio, isProcessingAction: isCreatingPortfolio } = usePortfolioCreation();


  // Derived state for filtered portfolios
  const filteredPortfolios = useMemo(() => {
    let baseFiltered = filterPortfolios(
      allPortfoliosFromFirebase || [],
      allBusinesses || [], // Pass allBusinesses
      filters.portfolios,
      filters.status,
      filters.industries,
      firebaseUser?.uid, 
      false // Display all portfolios user has access to, regardless of direct membership role
    );

    if (searchTerm) {
      baseFiltered = baseFiltered
        .map((portfolio) => {
          const portfolioBusinessObjects = (portfolio.businessRefs || [])
            .map(ref => (allBusinesses || []).find(b => b.id === ref))
            .filter(b => b !== undefined);

          if (portfolioBusinessObjects.length > 0) {
            const businessesMatchingSearch = portfolioBusinessObjects.filter((business) => {
              const nameMatch = business?.name
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase());
              const addressMatch = business?.address 
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase());
              return nameMatch || addressMatch;
            });
            return businessesMatchingSearch.length > 0 ? portfolio : null;
          }
          return null;
        })
        .filter(Boolean) as Portfolio[];
    }
    return baseFiltered;
  }, [allPortfoliosFromFirebase, allBusinesses, searchTerm, filters, firebaseUser?.uid]);


  // Event Handlers
  const handleSearch = (term: string) => setSearchTerm(term);
  const setLayoutMode = (mode: LayoutMode) => setLayoutModeState(mode);
  const handleFilterChange = (newFilters: FilterOptions) => setFilters(prev => ({ ...prev, ...newFilters }));
  const toggleFilterOptions = () => setShowFilterOptions(prev => !prev);
  const handleFilterApply = () => setShowFilterOptions(false);

  const closeAllModals = () => {
    setShowAddPortfolioModal(false);
    setShowAddBusinessModal(false);
    setShowMobileAddBusinessModal(false);
    setSelectedPortfolioId(null);
  };

  const openAddPortfolioModal = () => {
    closeAllModals();
    setShowAddPortfolioModal(true);
  };

  const handleAddNewPortfolio = () => { // This was from modalState, effectively same as openAddPortfolioModal
    openAddPortfolioModal();
  };
  
  const handleSelectPortfolio = (portfolioId: string) => {
    closeAllModals();
    setSelectedPortfolioId(portfolioId);
    setShowAddBusinessModal(true); // Or mobile version based on screen size - needs isMobile check
  };

  const handleShowMobileAddBusinessModal = () => {
    closeAllModals();
    // Potentially set selectedPortfolioId if needed for this flow, or it's handled by its own modal logic
    setShowMobileAddBusinessModal(true);
  };
  
  const getPortfolioNameById = (id: string | null) => {
    if (!id) return '';
    const portfolio = (allPortfoliosFromFirebase || []).find(p => p.id === id);
    return portfolio ? portfolio.name : ''; // Use portfolio.name
  };

  // This useEffect replaces the one from usePortfolioDebugLogging
  useEffect(() => {
    console.log("Index Page Debug:", {
      showAddPortfolioModal,
      showAddBusinessModal,
      showMobileAddBusinessModal,
      allPortfolios: allPortfoliosFromFirebase?.length
    });
  }, [showAddPortfolioModal, showAddBusinessModal, showMobileAddBusinessModal, allPortfoliosFromFirebase]);


  React.useEffect(() => {
    if (showAddBusinessModal && showAddPortfolioModal) { // Retained logic
      setShowAddPortfolioModal(false);
    }
  }, [showAddBusinessModal, showAddPortfolioModal, setShowAddPortfolioModal]);

  
  const navigateToDevTools = () => { // Retained
    navigate('/dev-tools');
  };

  // Wrapped handlePortfolioSubmit to use usePortfolioCreation
  const wrappedHandlePortfolioSubmit = async (formData: PortfolioFormValues) => {
    // Assuming PortfolioFormValues is compatible with PortfolioCreationInput ({ name: string })
    // If not, map formData to the expected input structure.
    const result = await createPortfolio({ name: formData.name });
    if (result.success && result.portfolioId) {
      // Optionally navigate or do something with result.portfolioId
      console.log("Portfolio created with ID:", result.portfolioId);
    }
    // The usePortfolioCreation hook handles toasts.
    return result.portfolioId; // Or just result.success
  };

  if (isLoadingData) { // Use isLoading from useFirebase
    return <LoadingScreen />;
  }
  
  if (error) {
    // Handle error state, e.g., show an error message
    return <div>Error loading data: {error.message}</div>;
  }

  return (
    <div className="min-h-screen bg-brand-light flex flex-col">
      <NavBar /> {/* NavBar might also need refactoring if it used useData */}
      <div className="container mx-auto px-4 pt-0 pb-8 bg-brand-light flex-1">
        <PendingInvitationsAlert /> {/* This component might also need refactoring */}
        
        <PortfolioPageLayout
          allBusinesses={allBusinesses || []} // Pass allBusinesses
          searchTerm={searchTerm}
          filters={filters}
          layoutMode={layoutMode}
          setLayoutMode={setLayoutMode}
          showMobileSearch={showMobileSearch}
          setShowMobileSearch={setShowMobileSearch}
          showFilterButton={showFilterButton} // This state needs to be managed
          setShowFilterButton={setShowFilterButton} // This state needs to be managed
          showFilterOptions={showFilterOptions}
          setShowFilterOptions={setShowFilterOptions}
          onSearch={handleSearch}
          onFilterChange={handleFilterChange}
          onFilterApply={handleFilterApply}
          onAddNewPortfolio={openAddPortfolioModal} // Use local handler
          onSelectPortfolio={handleSelectPortfolio} // Use local handler
          onShowMobileAddBusinessModal={handleShowMobileAddBusinessModal} // Use local handler
          allPortfolios={allPortfoliosFromFirebase || []}
          filteredPortfolios={filteredPortfolios}
          toggleFilterOptions={toggleFilterOptions}
        />
        
        <PortfolioModals
          showAddPortfolioModal={showAddPortfolioModal}
          setShowAddPortfolioModal={setShowAddPortfolioModal}
          showAddBusinessModal={showAddBusinessModal}
          setShowAddBusinessModal={setShowAddBusinessModal}
          showMobileAddBusinessModal={showMobileAddBusinessModal}
          setShowMobileAddBusinessModal={setShowMobileAddBusinessModal}
          selectedPortfolioId={selectedPortfolioId}
          getPortfolioNameById={getPortfolioNameById} // Use local handler
          handlePortfolioSubmit={wrappedHandlePortfolioSubmit} // Use local wrapped handler
          handleAddNewPortfolio={handleAddNewPortfolio} // Use local handler
          handleSelectPortfolio={handleSelectPortfolio} // Use local handler
          allPortfolios={allPortfoliosFromFirebase || []}
        />
      </div>
    </div>
  );
};

export default Index;
